import { Fragment, useState, useEffect } from 'react';
import { UserPlus, Trash2, RefreshCw, Shield, User, Mail, Calendar, CheckCircle, XCircle, CreditCard as Edit2, X, Save, Users, MailOpen, UserCog, Settings } from 'lucide-react';
import { userService } from '../services/userService';
import { chSupabase } from '../contenthub/lib/supabase';
import { chProfileService } from '../contenthub/services';
import { ROLE_LABELS as CH_ROLE_LABELS, type ChUserRole } from '../contenthub/types';
import { influencerService } from '../services/influencerService';
import type { UserRole, UserInvitation } from '../types/user';
import type { Influencer } from '../types/influencer';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { InviteStatusModal } from './InviteStatusModal';
import { InviteErrorModal } from './InviteErrorModal';
import { PendingInvitesManager } from './PendingInvitesManager';
import { UserStatusChecker } from './UserStatusChecker';
import { InfluencerAccountsPanel } from './InfluencerAccountsPanel';
import { AgencySettingsPanel } from './AgencySettingsPanel';
import { log } from '../lib/logger';
import { PermissionEditor, PermissionSummary } from './PermissionEditor';
import {
  PROFILE_PRESETS,
  NO_MAIN_APP_ACCESS,
  deriveProfile,
  pickPermissions,
  type StaffProfile,
} from '../lib/permissions';

interface AdminPanelProps {
  currentUserRole: UserRole | null;
}

// Profiles are templates: picking one sets every switch, and any switch can
// then be changed on its own (the person shows as "Personalizado").
type InviteProfile = StaffProfile | 'influencer' | 'content_hub';

export function AdminPanel({ currentUserRole }: AdminPanelProps) {
  const { t, translateError } = useLanguage();
  const { showConfirm, toast: showToast } = useModal();
  const [users, setUsers] = useState<UserRole[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRole | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'invites' | 'influencer-accounts' | 'settings'>('users');
  const [inviteResult, setInviteResult] = useState<{
    emailSent: boolean;
    email: string;
    inviteUrl: string;
    emailError?: { message: string; status?: number; name?: string } | null;
  } | null>(null);
  const [inviteError, setInviteError] = useState<{
    error: string;
    errorType?: string;
    technicalDetails?: {
      message: string;
      details?: string | null;
      stack?: string | null;
    };
    actionableSteps?: string[];
    timestamp?: string;
  } | null>(null);
  const [errorEmail, setErrorEmail] = useState<string>('');

  const [inviteProfile, setInviteProfile] = useState<InviteProfile>('agent');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    influencer_id: '',
    ...PROFILE_PRESETS.agent,
    role: 'agent' as UserRole['role'],
    ch_enabled: false,
    ch_role: '' as '' | 'admin' | 'project_manager' | 'client',
    ch_project_ids: [] as string[],
    name: '',
  });

  const applyInviteProfile = (profile: InviteProfile) => {
    setInviteProfile(profile);
    if (profile === 'content_hub') {
      setInviteForm(prev => ({
        ...prev,
        ...NO_MAIN_APP_ACCESS,
        role: 'agent',
        ch_enabled: true,
        ch_role: prev.ch_role || 'client',
      }));
      return;
    }
    if (profile === 'influencer') {
      setInviteForm(prev => ({ ...prev, role: 'influencer' }));
      return;
    }
    const preset = PROFILE_PRESETS[profile];
    setInviteForm(prev => ({
      ...prev,
      ...preset,
      role: preset.role as 'admin' | 'agent',
      // Content Hub follows the profile as a sensible default, still editable
      ch_enabled: profile !== 'agent' ? true : prev.ch_enabled,
      ch_role: profile === 'admin' ? 'admin' : profile === 'supervisor' ? 'project_manager' : prev.ch_role,
    }));
  };

  const [chProjects, setChProjects] = useState<{ id: string; name: string }[]>([]);
  // People with Content Hub access but no main-app role (e.g. clients)
  const [chOnlyUsers, setChOnlyUsers] = useState<{
    id: string; name: string; email: string; role: ChUserRole; projects: string[];
  }[]>([]);

  const loadContentHubOnlyUsers = async (mainAppUserIds: Set<string>) => {
    const [{ data: profiles }, { data: links }] = await Promise.all([
      chSupabase.from('ch_profiles').select('id, name, email, role').order('name'),
      chSupabase.from('ch_user_projects').select('user_id, project:ch_projects(name)'),
    ]);
    const projectsByUser = new Map<string, string[]>();
    for (const link of (links || []) as { user_id: string; project: { name: string } | { name: string }[] | null }[]) {
      const project = Array.isArray(link.project) ? link.project[0] : link.project;
      if (!project?.name) continue;
      projectsByUser.set(link.user_id, [...(projectsByUser.get(link.user_id) || []), project.name]);
    }
    setChOnlyUsers(((profiles || []) as { id: string; name: string; email: string; role: ChUserRole }[])
      .filter(p => !mainAppUserIds.has(p.id))
      .map(p => ({ ...p, projects: (projectsByUser.get(p.id) || []).sort() })));
  };

  const handleChRoleChange = async (userId: string, role: ChUserRole) => {
    try {
      await chProfileService.updateRole(userId, role);
      setChOnlyUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
      showToast.success('Papel no Content Hub atualizado. Aplica-se no próximo login da pessoa.');
    } catch {
      showToast.error('Não foi possível alterar o papel');
    }
  };

  const handleChRemoveAccess = async (target: { id: string; name: string }) => {
    const confirmed = await showConfirm({
      title: 'Remover acesso ao Content Hub?',
      message: `${target.name} deixa de entrar no Content Hub. Os comentários e aprovações que fez ficam guardados.`,
      confirmText: 'Remover acesso',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await chProfileService.removeAccess(target.id);
      setChOnlyUsers(prev => prev.filter(u => u.id !== target.id));
      showToast.success('Acesso removido');
    } catch {
      showToast.error('Não foi possível remover o acesso');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [usersData, invitationsData, influencersData] = await Promise.all([
      userService.getAllUsers(),
      userService.getInvitations(),
      influencerService.getAll()
    ]);
    setUsers(usersData);
    setInfluencers(influencersData);

    // Filter out invitations where user already exists or invitation was accepted
    const existingEmails = new Set(usersData.map(u => u.email.toLowerCase()));
    const pendingInvitations = invitationsData.filter(inv =>
      !inv.accepted && !existingEmails.has(inv.email.toLowerCase())
    );

    setInvitations(pendingInvitations);
    setLoading(false);

    // Best-effort: the list still works if the Content Hub can't be read
    loadContentHubOnlyUsers(new Set(usersData.map(u => u.user_id))).catch(() => setChOnlyUsers([]));

    // Load Content Hub projects (best-effort, don't block)
    chSupabase
      .from('ch_projects')
      .select('id, name')
      .eq('archived', false)
      .order('name')
      .then(({ data }) => { if (data) setChProjects(data); });
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    const contentHubOnly = inviteProfile === 'content_hub';
    if (contentHubOnly && !inviteForm.ch_role) {
      showToast.warning('Escolhe o papel no Content Hub');
      return;
    }
    try {
      const permissions = pickPermissions(inviteForm);

      const result = await userService.sendInvitation(
        inviteForm.email,
        contentHubOnly ? 'content_hub' : inviteForm.role,
        permissions,
        inviteForm.influencer_id || undefined,
        inviteForm.ch_enabled && inviteForm.ch_role ? inviteForm.ch_role : undefined,
        inviteForm.ch_enabled && inviteForm.ch_project_ids.length > 0 ? inviteForm.ch_project_ids : undefined,
        inviteForm.name.trim() || undefined
      );

      setInviteResult({
        emailSent: result.emailSent,
        email: result.email,
        inviteUrl: result.inviteUrl,
        emailError: result.emailError
      });

      const maskedEmail = maskEmail(result.email);
      if (result.emailSent) {
        showToast.success(`Convite enviado para ${maskedEmail}`);
      } else {
        showToast.warning(`Convite criado para ${maskedEmail}, mas o email não foi enviado. Copia o link e envia-o tu.`, 8000);
      }

      setShowInviteForm(false);
      setInviteProfile('agent');
      setInviteForm(prev => ({
        ...prev,
        email: '',
        influencer_id: '',
        ...PROFILE_PRESETS.agent,
        ch_enabled: false,
        ch_role: '' as const,
        ch_project_ids: [],
        name: '',
      }));
      await loadData();
    } catch (error: any) {
      log.error('Error sending invitation:', error);
      setErrorEmail(inviteForm.email);

      // Check if error response has structured error data
      if (error.context?.body) {
        const errorBody = error.context.body;
        setInviteError(errorBody);

        // Show error toast based on error type; actionable steps stay in InviteErrorModal
        const errorCode = errorBody.errorType || 'UNKNOWN';

        if (errorCode === 'CORS_FORBIDDEN') {
          showToast.error(`Origem não permitida. Pede para adicionar ${window.location.origin} a ALLOWED_ORIGINS na função invite-user.`, 8000);
        } else if (errorCode === 'NETWORK_ERROR') {
          showToast.error('Erro de rede/CORS. Verifica se esta origem está em ALLOWED_ORIGINS.', 8000);
        } else if (errorCode === 'VALIDATION_ERROR') {
          showToast.warning(errorBody.error || 'Validação falhou');
        } else {
          showToast.error(errorBody.error || 'Erro ao enviar convite');
        }
      } else {
        // Fallback for non-structured errors
        setInviteError({
          error: translateError(error),
          errorType: 'UNKNOWN',
          technicalDetails: {
            message: error.message || 'Unknown error',
            details: error.details || null,
            stack: error.stack || null
          },
          actionableSteps: [
            'Check the error details below',
            'Contact system administrator if the issue persists'
          ]
        });

        showToast.error(translateError(error));
      }
      setShowInviteForm(false);
    }
  };

  const handleUpdateUser = async (userId: string, updates: Partial<UserRole>) => {
    try {
      await userService.updateUserRole(userId, updates);
      await loadData();
      setEditingUser(null);
      showToast.success(t('admin.toast.userUpdatedSuccess'));
    } catch (error) {
      log.error('Error updating user:', error);
      showToast.error(t('admin.toast.userUpdateFailed'));
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    const confirmed = await showConfirm({
      title: 'Delete User?',
      message: `Are you sure you want to delete ${userEmail}? This will permanently remove their account and all associated data.`,
      confirmText: 'Delete User',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await userService.deleteUser(userId);
      showToast.success(t('admin.toast.userDeletedSuccess'));
      await loadData();
    } catch (error) {
      log.error('Error deleting user:', error);
      showToast.error(t('admin.toast.userDeleteFailed'));
    }
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    try {
      await userService.deleteInvitation(invitationId);
      showToast.success(t('admin.toast.invitationDeletedSuccess'));
      await loadData();
    } catch (error) {
      log.error('Error deleting invitation:', error);
      showToast.error(t('admin.toast.invitationDeleteFailed'));
    }
  };

  if (!currentUserRole?.can_manage_users) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-500 text-lg">{t('admin.noPermission')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
        <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
        <p className="text-gray-500 text-lg">{t('admin.loadingPanel')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'users'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-5 h-5" />
              {t('admin.usersTab')}
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'invites'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MailOpen className="w-5 h-5" />
              {t('admin.invitesTab')}
              {invitations.length > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                  {invitations.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('influencer-accounts')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'influencer-accounts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserCog className="w-5 h-5" />
              {t('admin.creatorAccountsTab')}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'settings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-5 h-5" />
              {t('admin.settings.tab')}
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'invites' ? (
        <PendingInvitesManager />
      ) : activeTab === 'influencer-accounts' ? (
        <InfluencerAccountsPanel />
      ) : activeTab === 'settings' ? (
        <AgencySettingsPanel />
      ) : (
        <div className="space-y-6">
          <UserStatusChecker />

          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('admin.userManagement')}</h2>
            <p className="text-gray-600 mt-1">{t('admin.manageAccess')}</p>
          </div>
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="flex items-center gap-2 px-6 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            {t('admin.inviteUser')}
          </button>
        </div>

        {showInviteForm && (
          <form onSubmit={handleSendInvitation} className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.sendInvitation')}</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.email')}</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder={t('admin.emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.profile')}</label>
                <select
                  value={inviteProfile}
                  onChange={(e) => applyInviteProfile(e.target.value as InviteProfile)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                >
                  <option value="agent">{t('admin.roleAgent')}</option>
                  <option value="supervisor">{t('admin.roleSupervisor')}</option>
                  <option value="admin">{t('admin.roleAdmin')}</option>
                  <option value="influencer">{t('admin.roleInfluencer')}</option>
                  <option value="content_hub">{t('admin.roleContentHubOnly')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5">
                  {inviteProfile === 'admin' && t('admin.profileAdminDesc')}
                  {(inviteProfile === 'supervisor' || inviteProfile === 'agent') && (
                    deriveProfile(inviteForm) === 'custom'
                      ? t('admin.profileCustomDesc')
                      : t(inviteProfile === 'supervisor' ? 'admin.profileSupervisorDesc' : 'admin.profileAgentDesc')
                  )}
                  {inviteProfile === 'influencer' && t('admin.profileInfluencerDesc')}
                  {inviteProfile === 'content_hub' && t('admin.profileContentHubDesc')}
                </p>
              </div>
            </div>

            {inviteProfile === 'content_hub' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.inviteName')}</label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                  placeholder={t('admin.inviteNamePlaceholder')}
                />
              </div>
            )}

            {inviteForm.role === 'influencer' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('admin.selectInfluencer')} *
                  </label>
                  <select
                    required
                    value={inviteForm.influencer_id}
                    onChange={(e) => setInviteForm({ ...inviteForm, influencer_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">{t('admin.selectInfluencerPlaceholder')}</option>
                    {influencers
                      .filter(inf => !inf.user_id)
                      .map(influencer => (
                        <option key={influencer.id} value={influencer.id}>
                          {influencer.full_name}
                        </option>
                      ))
                    }
                  </select>
                  {influencers.filter(inf => !inf.user_id).length === 0 && (
                    <p className="text-sm text-red-600 mt-2">
                      {t('admin.noAvailableInfluencers')}
                    </p>
                  )}
                </div>
                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-800">
                    <strong>{t('admin.influencerNote')}:</strong> {t('admin.influencerNoteText')}
                  </p>
                </div>
              </>
            )}

            {(inviteProfile === 'supervisor' || inviteProfile === 'agent') && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800 mb-2">{t('admin.permissions')}</p>
                <PermissionEditor
                  value={pickPermissions(inviteForm)}
                  onChange={(next) => setInviteForm(prev => ({ ...prev, ...next }))}
                />
              </div>
            )}

            {/* Content Hub access section */}
            {inviteForm.role !== 'influencer' && (
              <div className="mt-5 p-4 rounded-lg bg-blue-50/50 border border-blue-100">
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={inviteForm.ch_enabled}
                    disabled={inviteProfile === 'content_hub'}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setInviteForm({
                        ...inviteForm,
                        ch_enabled: enabled,
                        ch_role: enabled
                          ? (inviteForm.role === 'admin' ? 'admin' : 'project_manager')
                          : '',
                        ch_project_ids: [],
                      });
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-semibold text-gray-800">Acesso ao Content Hub</span>
                </label>

                {inviteForm.ch_enabled && (
                  <div className="space-y-3 mt-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Papel no Content Hub</label>
                      <select
                        value={inviteForm.ch_role}
                        onChange={(e) => setInviteForm({ ...inviteForm, ch_role: e.target.value as any })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="admin">Admin — tudo, incluindo utilizadores</option>
                        <option value="project_manager">Account — edita conteúdo, stats e links</option>
                        <option value="client">Cliente — vê os seus projetos, comenta e aprova</option>
                      </select>
                    </div>

                    {inviteForm.ch_role === 'client' && chProjects.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Projetos que o cliente vê</label>
                        <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-white rounded-lg border border-gray-200">
                          {chProjects.map(p => (
                            <label key={p.id} className="flex items-center gap-2 py-0.5">
                              <input
                                type="checkbox"
                                checked={inviteForm.ch_project_ids.includes(p.id)}
                                onChange={(e) => {
                                  const ids = e.target.checked
                                    ? [...inviteForm.ch_project_ids, p.id]
                                    : inviteForm.ch_project_ids.filter(id => id !== p.id);
                                  setInviteForm({ ...inviteForm, ch_project_ids: ids });
                                }}
                                className="w-3.5 h-3.5 text-blue-600"
                              />
                              <span className="text-xs text-gray-700">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                type="submit"
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-transform hover:scale-[1.02]"
              >
                {t('admin.sendInvitationButton')}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('admin.activeUsers').replace('{0}', String(users.length))}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.userTableUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.userTableRole')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.userTablePermissions')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin.userTableActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => {
                const editing = editingUser?.user_id === user.user_id ? editingUser : null;
                const editingProfile = editing ? deriveProfile(editing) : null;
                return (
                <Fragment key={user.id}>
                <tr className={editing ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        user.role === 'admin' ? 'bg-blue-100' : user.role === 'influencer' ? 'bg-purple-100' : user.role === 'agent' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {user.role === 'admin' ? (
                          <Shield className="w-4 h-4 text-blue-600" />
                        ) : user.role === 'influencer' ? (
                          <User className="w-4 h-4 text-purple-600" />
                        ) : user.role === 'agent' ? (
                          <User className="w-4 h-4 text-green-600" />
                        ) : (
                          <User className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing ? (
                      <select
                        value={editingProfile!}
                        onChange={(e) => {
                          const p = e.target.value as StaffProfile | 'influencer';
                          if (p === 'influencer') {
                            setEditingUser({ ...editing, role: 'influencer' });
                          } else {
                            setEditingUser({ ...editing, ...PROFILE_PRESETS[p] });
                          }
                        }}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                      >
                        <option value="agent">{t('admin.roleAgent')}</option>
                        <option value="supervisor">{t('admin.roleSupervisor')}</option>
                        {editingProfile === 'custom' && <option value="custom" disabled>{t('admin.roleCustom')}</option>}
                        <option value="admin">{t('admin.roleAdmin')}</option>
                        <option value="influencer">{t('admin.roleInfluencer')}</option>
                      </select>
                    ) : (
                      (() => {
                        const profile = deriveProfile(user);
                        const styles: Record<string, string> = {
                          admin: 'bg-blue-100 text-blue-800',
                          supervisor: 'bg-amber-100 text-amber-800',
                          agent: 'bg-green-100 text-green-800',
                          custom: 'bg-gray-100 text-gray-800',
                          influencer: 'bg-purple-100 text-purple-800',
                        };
                        const labels: Record<string, string> = {
                          admin: t('admin.roleAdmin'),
                          supervisor: t('admin.roleSupervisor'),
                          agent: t('admin.roleAgent'),
                          custom: t('admin.roleCustom'),
                          influencer: t('admin.roleInfluencer'),
                        };
                        return (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[profile]}`}>
                            {labels[profile]}
                          </span>
                        );
                      })()
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editing && editingProfile === 'admin' ? (
                      <div className="space-y-1.5">
                        <span className="block text-xs text-gray-500">{t('admin.profileAdminDesc')}</span>
                        <label className="flex items-center gap-1.5 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!editing.can_issue_invoices}
                            onChange={(e) => setEditingUser({ ...editing, can_issue_invoices: e.target.checked })}
                            className="w-3.5 h-3.5 text-accent-600 rounded"
                          />
                          {t('admin.permIssueInvoices')} <span className="text-gray-400">· {t('admin.permIssueInvoicesHint')}</span>
                        </label>
                      </div>
                    ) : editing ? (
                      <span className="text-xs text-gray-500">
                        {editing.role === 'influencer'
                          ? t('admin.profileInfluencerDesc')
                          : editingProfile === 'admin'
                          ? t('admin.profileAdminDesc')
                          : editingProfile === 'supervisor'
                          ? t('admin.profileSupervisorDesc')
                          : editingProfile === 'agent'
                          ? t('admin.profileAgentDesc')
                          : t('admin.profileCustomDesc')}
                      </span>
                    ) : user.role === 'admin' ? (
                      <div className="flex flex-wrap gap-1">
                        <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded">{t('admin.fullAccess')}</span>
                        {user.can_issue_invoices && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">{t('admin.permIssueInvoices')}</span>
                        )}
                      </div>
                    ) : user.role === 'influencer' ? (
                      <span className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded">Creators Hub</span>
                    ) : (
                      <PermissionSummary value={pickPermissions(user)} emptyLabel={t('admin.baseAccess')} />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {editing ? (
                        <>
                          <button
                            onClick={() => handleUpdateUser(user.user_id, { role: editing.role, ...pickPermissions(editing) })}
                            className="text-green-600 hover:text-green-900"
                            title={t('admin.saveChanges')}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="text-gray-600 hover:text-gray-900"
                            title={t('admin.cancelEdit')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingUser(user)}
                            disabled={user.email === 'guilherme@mohit.pt'}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('admin.editPermissions')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.user_id, user.email)}
                            disabled={user.email === 'guilherme@mohit.pt'}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={t('admin.deleteUserButton')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {editing && editing.role !== 'influencer' && editingProfile !== 'admin' && (
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="px-6 pb-5 pt-1">
                      <PermissionEditor
                        value={pickPermissions(editing)}
                        onChange={(next) => setEditingUser({ ...editing, ...next })}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {chOnlyUsers.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('admin.contentHubOnlyUsers').replace('{0}', String(chOnlyUsers.length))}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{t('admin.contentHubOnlyUsersHint')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.userTableUser')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.contentHubRole')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.contentHubProjects')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin.userTableActions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {chOnlyUsers.map(chUser => (
                  <tr key={chUser.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-lime-100">
                          <User className="w-4 h-4 text-lime-700" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{chUser.name}</div>
                          <div className="text-xs text-gray-500">{chUser.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={chUser.role === 'talent' ? 'client' : chUser.role}
                        onChange={(e) => handleChRoleChange(chUser.id, e.target.value as ChUserRole)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500"
                      >
                        <option value="admin">{CH_ROLE_LABELS.admin}</option>
                        <option value="project_manager">{CH_ROLE_LABELS.project_manager}</option>
                        <option value="client">{CH_ROLE_LABELS.client}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      {chUser.role === 'admin' || chUser.role === 'project_manager' ? (
                        <span className="text-xs text-gray-500">{t('admin.contentHubAllProjects')}</span>
                      ) : chUser.projects.length === 0 ? (
                        <span className="text-xs text-amber-700">{t('admin.contentHubNoProjects')}</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {chUser.projects.map(name => (
                            <span key={name} className="px-2 py-0.5 text-xs rounded-md bg-gray-100 text-gray-700">{name}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleChRemoveAccess(chUser)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('admin.contentHubRemoveAccess')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {invitations.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('admin.pendingInvitations').replace('{0}', String(invitations.length))}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.invitationEmail')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.invitationRole')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.invitationStatus')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.invitationExpires')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('admin.invitationActions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{invitation.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        invitation.role === 'admin'
                          ? 'bg-blue-100 text-blue-800'
                          : invitation.role === 'influencer'
                          ? 'bg-purple-100 text-purple-800'
                          : invitation.role === 'agent'
                          ? 'bg-green-100 text-green-800'
                          : invitation.role === 'content_hub'
                          ? 'bg-lime-100 text-lime-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {invitation.role === 'admin' ? t('admin.roleAdmin') : invitation.role === 'influencer' ? t('admin.roleInfluencer') : invitation.role === 'agent' ? t('admin.roleAgent') : invitation.role === 'content_hub' ? t('admin.roleContentHubOnly') : invitation.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invitation.accepted ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {t('admin.statusAccepted')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-orange-600 text-sm">
                          <XCircle className="w-4 h-4" />
                          {t('admin.statusPending')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(invitation.expires_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleDeleteInvitation(invitation.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      )}

      <InviteStatusModal
        isOpen={inviteResult !== null}
        onClose={() => setInviteResult(null)}
        emailSent={inviteResult?.emailSent ?? false}
        email={inviteResult?.email ?? ''}
        inviteUrl={inviteResult?.inviteUrl ?? ''}
        emailError={inviteResult?.emailError}
      />

      <InviteErrorModal
        isOpen={inviteError !== null}
        onClose={() => {
          setInviteError(null);
          setErrorEmail('');
        }}
        error={inviteError ?? { error: '' }}
        email={errorEmail}
      />

    </div>
  );
}

// Helper function to mask email for display
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;

  if (local.length <= 2) {
    return `${local}***@${domain}`;
  }

  return `${local.substring(0, 2)}***@${domain}`;
}
